<template>
    <div ref="header">
        <div class="display-5 text-truncate me-5">{{ $tm(data?.title ?? '') }}</div>
        <div class="display-6 text-truncate me-5">{{ $tm(data?.subtitle ?? '') }}</div>
    </div>
    <div ref="chart"></div>
</template>

<script setup lang="ts" generic="T extends Date">
/**
 * This is a generic LineChart component that can be used to display line charts
 * TODO: For the moment it only supports Date as the x-axis, this should be changed to support any type
 *
 * @see https://plotly.com/javascript/
 * @see https://www.npmjs.com/package/plotly.js-dist-min
 * @see https://www.npmjs.com/package/@types/plotly.js-dist-min
 *
 * @note The types are imported in the wrong folder. Need to rename the folder to @types/plotly.js-dist
 *
 */
import { onMounted, ref, watch, nextTick } from 'vue';
import Plotly, { type PlotlyHTMLElement } from 'plotly.js-dist-min';
import { LineChartData } from './LineChartData';
import { $tm } from '@/locize';
import { useThemeStoreRef } from '@/stores';
import { type ILineChartExpose } from './ILineChartExpose';

// We need to get the currentTheme s owe can listen for theme changes to update the chart colors
const { currentTheme } = useThemeStoreRef();

// The chart ref
const chart = ref<PlotlyHTMLElement | null>(null);

// The header ref
const header = ref<HTMLElement | null>(null);

/// The props for the component
const props = withDefaults(
    defineProps<{
        /**
         * The data to display in the chart
         */
        data: LineChartData<T> | null;
        /**
         * If the chart should be displayed in fullscreen mode
         * Then we will also show the toolbar
         * @default false
         */
        fullscreen?: boolean;

        /**
         * The hovertemplate for the chart
         * @default '%{y:.0f}<extra></extra>'
         */
        hovertemplate?: string;

        /**
         * The height of the chart
         * @default 'auto'
         */
        height?: 'auto' | number;

        /**
         * The type of the chart
         * @default 'line'
         */
        type?: 'line' | 'bar';

        /**
         * If we should show markers on the line
         * @default false
         */
        markers?: boolean;
    }>(),
    {
        data: null,
        fullscreen: false,
        hovertemplate: '%{y:.0f}<extra></extra>',
        height: 'auto',
        type: 'line',
        markers: false,
    },
);

// We need to keep track of the previous data so we can update the chart with the diff from new data
let previousData: LineChartData<T> | null = null;

// The initial height of the chart when it is drawn the first time
let initialHeight: number = props.height === 'auto' ? 0 : props.height;

// The shape of the line
// We can set this to 'linear' if we want a straight line
const shape = props.type === 'line' ? 'linear' : 'hvh';

const markers = props.markers ? '+markers' : '';

/**
 * Create the Plotly instance
 * @param data
 */
const createPlotlyInstance = (data: LineChartData<T>, relayout: boolean = true) => {
    if (!chart.value || data.datasets?.length === 0) {
        return;
    }

    Plotly.react(
        chart.value!,
        data.datasets?.map((d) => ({
            x: d.xaxis,
            y: d.yaxis,
            mode: 'lines' + markers,
            name: $tm(d.legend),
            line: { shape: shape },
            hovertemplate: props.hovertemplate,
        })) ?? [],
        layout,
        config,
    );

    if (relayout) {
        setTimeout(() => {
            Plotly.relayout(chart.value!, layout);
        }, 100);
    }
};

/**
 * Update the chart data
 * We find the diff between the previous data and the new data and then update the chart
 * @param newdata
 */
const updateChartData = (newdata: LineChartData<T> | null) => {
    if (newdata === null) {
        return;
    }

    let isAllEmpty = true;
    let minXaxisValue = new Date();
    newdata.datasets.forEach((d, i) => {
        if (d.xaxis.length !== 0 && d.yaxis.length !== 0) {
            isAllEmpty = false;
        }
        // We know the smallest date is the first
        if (d.xaxis.length > 0) {
            minXaxisValue = d.xaxis[0] < minXaxisValue ? d.xaxis[0] : minXaxisValue;
        }
    });

    if (newdata.datasets.length === 0 || isAllEmpty) {
        // If we send in an empty dataset we will just clear the chart
        createPlotlyInstance(newdata, false);
        setPreviousData(newdata);
        Plotly.relayout(chart.value!, layout);
    } else {
        // We need to extendTraces with the diff between the previousData and newdata
        // We always assume the new dataset is larger than the previous and add the diff to the trace
        // If we want to draw a new line we first need to clear the chart by sending in an empty dataset and then add the new data
        if (previousData !== null) {
            newdata?.datasets.forEach((d, i) => {
                let xaxisdiff = d.xaxis.slice(previousData!.datasets[i].xaxis.length);
                let yaxisdiff = d.yaxis.slice(previousData!.datasets[i].yaxis.length);

                Plotly.extendTraces(chart.value!, { x: [xaxisdiff], y: [yaxisdiff] }, [i]);
            });
        } else {
            createPlotlyInstance(newdata, false);
        }

        // Save the previous state so we can compare later
        setPreviousData(newdata);

        layout.xaxis!.range = [minXaxisValue, new Date()];
        layout.xaxis!.autorange = false;
        Plotly.relayout(chart.value!, layout);
    }
};

/**
 * Helper method to deepclone the newdata and set it as the previousData
 * @param newdata
 */
const setPreviousData = (newdata: LineChartData<T> | null) => (previousData = JSON.parse(JSON.stringify(newdata)));

/**
 * Set upe the Layout options
 */
const layout = {
    margin: { t: 10, b: 60, l: 40, r: 10 },
    paper_bgcolor: currentTheme.value == 'dark' ? '#00000000' : '#ffffff00',
    plot_bgcolor: currentTheme.value == 'dark' ? '#00000033' : '#f8f8f8',
    showlegend: true,
    hovermode: 'x',
    hoverdistance: 100,
    dragmode: 'zoom',
    autosize: props.height === 'auto',
    height: props.height === 'auto' ? undefined : props.height,
    legend: {
        x: 1,
        xanchor: 'right',
        y: 1,
    },
    autoscale: false,
    modebar: {
        orientation: 'h',
    },

    yaxis: {
        tickcolor: 'rgba(255,0,0,0.75)',
        tickwidth: 2,
        gridcolor: 'rgba(255,0,0,0.25)',
        gridwidth: 1,
        zerolinecolor: '#666666',
        zerolinewidth: 1,
        rangemode: 'tozero',
    },
    xaxis: {
        tickcolor: 'rgba(255,0,0,0.75)',
        tickwidth: 2,

        gridcolor: 'rgba(255,0,0,0.25)',
        gridwidth: 1,

        autorange: true,
    },
} as Partial<Plotly.Layout>;

/**
 * Default config for the chart
 */
const config = {
    responsive: true,
    displaylogo: false,
    displayModeBar: false,
    autosizable: true,
} as Partial<Plotly.Config>;

/**
 * Set the theme colors
 * @param nv
 */
const setThemeColors = () => {
    layout.paper_bgcolor = currentTheme.value == 'dark' ? '#00000000' : '#ffffff00';
    layout.plot_bgcolor = currentTheme.value == 'dark' ? '#00000033' : '#f8f8f8';
    Plotly.relayout(chart.value!, layout);
};

/**
 * Set the fullscreen mode
 * We will show the toolbar if the fullscreen is true
 * @param nv
 */
const setFullScreen = (nv: boolean) => {
    if (!chart.value) return;

    // if nv is true we set the height to the element parent innerHeight or else we set it back to the initial height
    // we need to do this in the nextTick so the parentElement is properly set
    nextTick(() => {
        let parentInnerHeight =
            chart.value!.parentElement?.clientHeight! -
            header.value!.clientHeight! -
            parseFloat(getComputedStyle(chart.value!.parentElement!).paddingTop) -
            parseFloat(getComputedStyle(chart.value!.parentElement!).paddingBottom);

        let parentInnerWidth =
            chart.value!.parentElement?.clientWidth! -
            parseFloat(getComputedStyle(chart.value!.parentElement!).paddingLeft) -
            parseFloat(getComputedStyle(chart.value!.parentElement!).paddingRight);

        layout.height = nv ? parentInnerHeight : initialHeight;
        layout.width = parentInnerWidth;

        config.displayModeBar = nv;
        layout.margin!.t = nv ? 25 : 10;

        createPlotlyInstance(props.data!);
    });
};

/**
 * Reset the chart to the initial state
 */
const resetChart = () => {
    if (!chart.value) return;
    // Reset the previous data
    previousData = null;

    Plotly.purge(chart.value);
    createPlotlyInstance(props.data!, true);
};

/**
 * Watch the data for changes and then update the chart
 */
watch(
    () => props.data!,
    (newvalue) => updateChartData(newvalue),
    { deep: true },
);

/**
 * Watch the theme for changes and then update the chart layout
 */
watch(currentTheme, () => setThemeColors());

/**
 * Watch the fullscreen prop and then set the fullscreen mode
 */
watch(
    () => props.fullscreen,
    (nv) => setFullScreen(nv),
);

/**
 * Create the chart when the component is mounted
 */
onMounted(() => {
    if (!chart.value) return;

    // Set the current data as the previous data to make sure we can update the chart with the diff
    setPreviousData(props.data!);

    // Create the Plotly instance
    createPlotlyInstance(props.data!);

    // We keep the initial height so we can resize the chart when the window is resized
    initialHeight = chart.value.getBoundingClientRect().height;

    // Add the event listener for the relayout event
    // Not sure we need this one yet
    // chart.value.on('plotly_relayout', (eventdata: any) => {});
});

defineExpose<ILineChartExpose>({
    Reset: resetChart,
});
</script>
